import React, { FC, useEffect, useRef, useState } from "react";
import Box from "../Box";
import IconButton from "../IconButton";
import { AddIcon, RemoveIcon } from "../Icons";
import useStyles from "./useStyles";

const RECALCULATION_DEBOUNCE_MS = 500;

export type NumberSpinnerProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  disabled?: boolean;
};

const NumberSpinner: FC<NumberSpinnerProps> = ({
  value,
  onChange,
  min = 1,
  disabled,
}) => {
  const classes = useStyles();
  const [displayValue, setDisplayValue] = useState(value);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const commitChange = (nextValue: number) => {
    setDisplayValue(nextValue);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      onChange(nextValue);
    }, RECALCULATION_DEBOUNCE_MS);
  };

  return (
    <Box className={classes.root}>
      <IconButton
        className={`${classes.button} ${classes.buttonLeft}`}
        onClick={() => commitChange(Math.max(min, displayValue - 1))}
        disabled={disabled || displayValue <= min}
        size="small"
        aria-label="decrease quantity"
      >
        <RemoveIcon fontSize="small" />
      </IconButton>
      <Box className={classes.value}>{displayValue}</Box>
      <IconButton
        className={`${classes.button} ${classes.buttonRight}`}
        onClick={() => commitChange(displayValue + 1)}
        disabled={disabled}
        size="small"
        aria-label="increase quantity"
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default NumberSpinner;
